import { useSearchParams } from 'react-router-dom'
import { faPencil, faCheck } from '@fortawesome/pro-light-svg-icons'
import moment from 'moment'

import { Button, Icon, ButtonProps } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { CSSProperties } from 'styled-components'
import Lottie from 'lottie-react'

// TODO: Migrate to V2

export const CONNECT_AND_CLARIFY_URL =
  'https://calendly.com/today-at-heard/connect-and-clarify'

export const CALENDLY_RESCHEDULE_URL_BASE =
  'https://calendly.com/reschedulings/'

// these values are pulled from the DB under the `action_items.identifier` column
export enum OnboardingTaskIdents {
  // HelloSign package modal
  SIGN_BAA_AGREEMENT = 'sign-baa-agreement',
  SCHEDULE_ONBOARDING_MEETING = 'schedule-onboarding-meeting',
  // File upload for prev month statements
  UPLOAD_PREV_MONTH_STATEMENTS = 'upload-prev-month-statements',
  // Nav to accounts/new
  CONNECT_A_BUSINESS_BANK_ACCOUNT = 'connect-a-business-bank-account',
}

export enum OnboardingV2TaskIdents {
  // HelloSign package modal
  SIGN_BAA_AGREEMENT = OnboardingTaskIdents.SIGN_BAA_AGREEMENT,
  // File upload for prev month statements
  UPLOAD_PREV_MONTH_STATEMENTS = OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
  // Nav to accounts/new
  CONNECT_A_BUSINESS_BANK_ACCOUNT = OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT,
}

export enum GuidedOnboardingTaskIdents {
  SIGN_BAA_AGREEMENT = OnboardingTaskIdents.SIGN_BAA_AGREEMENT,
  // File upload for prev month statements
  UPLOAD_PREV_MONTH_STATEMENTS = OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
  // Nav to accounts/new
  CONNECT_A_BUSINESS_BANK_ACCOUNT = OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT,
  SCHEDULE_ONBOARDING_MEETING = OnboardingTaskIdents.SCHEDULE_ONBOARDING_MEETING,
  FINANCIAL_ACCOUNT_QUESTIONS = 'financial-account-questions',
}

export enum PostOnboardingTaskIdents {
  // Nav to profile edit
  FILL_OUT_BUSINESS_PROFILE = 'fill-out-business-profile',
  // PayrollAccessModal
  SET_UP_PAYROLL_ACCESS = 'set-up-payroll-access',
  // File upload for previous year personal tax return
  UPLOAD_TAX_RETURN_1040 = 'upload-tax-return-1040',
  // File upload for previous year business tax return
  UPLOAD_TAX_RETURN_1120S = 'upload-tax-return-1120s',
  // OnboardingEstimatedTaxPaymentsModal
  ENTER_QTE_PAYMENTS = 'enter-qte-payments',
  // Navigate to allocation creation
  SET_UP_HOW_YOUR_EARNINGS_ARE_ALLOCATED = 'set-up-how-your-earnings-are-allocated',
  GET_TO_KNOW_HEARD = 'get-to-know-heard',
  // nav to /taxes/profile
  UPDATE_TAX_PROFILE = 'update-tax-profile',
  // File upload for business_incorporation
  UPLOAD_BUSINESS_INCORPORATION_DOCUMENTS = 'upload-business-incorporation-documents',
  // File upload for cp261_notice
  UPLOAD_CP261_NOTICE = 'upload-cp261-notice',
  // File upload for form_2553
  UPLOAD_FORM_2553 = 'upload-form-2553',
  // Nav to separate transactions
  SEPARATE_BUSINESS_AND_PERSONAL_TRANSACTIONS = 'separate-business-and-personal-transactions',
}

export enum ONBOARDING_V2_ROUTE_NAMES {
  ONBOARDING_COMPLETE = '/onboarding-v2/complete',
}
export const prevYearMonth = moment()
  .subtract(1, 'month')
  .format(DATE_FORMATS.MONTH_YEAR)

export const templateDescription = (
  description: string,
  templateValue?: string
) => (templateValue ? description.replace(/%s/g, templateValue) : description)

export const TaskCTAButton = ({
  completedAt,
  completedText,
  disabled,
  onClick,
  variant,
  text,
  isEditable,
  loading,
}: {
  completedAt?: string
  completedText: string
  disabled?: boolean
  onClick: () => void
  variant?: ButtonProps['variant']
  text?: string
  isEditable?: boolean
  loading?: boolean
}) => (
  <Button
    disabled={disabled}
    loading={loading}
    className={
      completedAt
        ? 'bg-transparent text-neutral-900 border border-neutral-900'
        : ''
    }
    onClick={onClick}
    variant={variant ?? 'primary'}
  >
    {completedAt ? (
      <>
        <Icon
          icon={isEditable ? faPencil : faCheck}
          style={{ marginRight: 8 }}
        />
        {isEditable ? 'Edit' : completedText}
      </>
    ) : (
      (text ?? 'Start')
    )}
  </Button>
)

export const ModalAnimation = ({
  url,
  title,
  style,
}: {
  url: string
  title: string
  style?: CSSProperties
}) => (
  <iframe
    style={{
      border: 'none',
      ...style,
    }}
    width="200"
    height="200"
    src={url}
    title={title}
    sandbox="allow-scripts"
  />
)

export const ModalLottieAnimation = ({
  animationData,
  loop,
}: {
  animationData: object
  loop: boolean
}) => (
  <Lottie
    style={{
      width: '200px',
      height: '200px',
      marginLeft: 'auto',
      marginRight: 'auto',
    }}
    animationData={animationData}
    loop={loop}
  />
)

export const useIsOnboardingNav = () => {
  const [searchParams] = useSearchParams()
  return searchParams.get('onboarding') === 'true'
}
